import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Pencil, UserMinus, UserPlus, ChevronDown, History } from 'lucide-react';

const Row = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm text-slate-800">{value || '—'}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-3">{title}</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">{children}</div>
  </div>
);

const money = (v) => (v === null || v === undefined ? null : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`);

export default function ColaboradorDetailDialog({ open, onClose, colaborador, canFull, onEdit, onBaja, onReingreso }) {
  const [histOpen, setHistOpen] = useState(false);
  if (!colaborador) return null;

  const periods = colaborador.periodos_laborales || [];
  const active = periods.length && !periods[periods.length - 1].fecha_baja ? periods[periods.length - 1] : null;
  const history = active ? periods.slice(0, -1) : periods;
  const isActivo = colaborador.estado === 'activo';
  const ce = colaborador.contacto_emergencia || {};
  const b1 = colaborador.beneficiario_1 || {};
  const b2 = colaborador.beneficiario_2 || {};
  const hasSalario = active && Object.prototype.hasOwnProperty.call(active, 'salario_diario');
  const benefText = (b) => (b && b.nombre ? `${b.nombre} (${b.parentesco || '—'}) ${b.porcentaje != null ? b.porcentaje + '%' : ''}${b.fecha_nacimiento ? ' · ' + b.fecha_nacimiento : ''}` : null);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="colab-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>{colaborador.nombre_completo}</span>
            <Badge className={isActivo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'} data-testid="colab-detail-estado">
              {isActivo ? 'Activo' : 'Baja'}
            </Badge>
            <span className="text-xs text-slate-400 font-normal">Nómina: {colaborador.numero_nomina}</span>
          </DialogTitle>
        </DialogHeader>

        {canFull && (
          <div className="flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={() => onEdit(colaborador)} data-testid="colab-detail-edit">
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
            {isActivo ? (
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => onBaja(colaborador)} data-testid="colab-detail-baja">
                <UserMinus className="w-4 h-4 mr-1" /> Dar de baja
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onReingreso(colaborador)} data-testid="colab-detail-reingreso">
                <UserPlus className="w-4 h-4 mr-1" /> Reingreso
              </Button>
            )}
          </div>
        )}

        <Section title="Identidad">
          <Row label="CURP" value={colaborador.curp} />
          <Row label="RFC" value={colaborador.rfc} />
          <Row label="NSS" value={colaborador.nss} />
          <Row label="Fecha nacimiento" value={colaborador.fecha_nacimiento} />
          <Row label="Edad" value={colaborador.edad != null ? `${colaborador.edad} años` : null} />
          <Row label="Lugar nacimiento" value={colaborador.lugar_nacimiento} />
          <Row label="Sexo" value={colaborador.sexo} />
          <Row label="Estado civil" value={colaborador.estado_civil} />
          <Row label="Escolaridad" value={colaborador.escolaridad} />
        </Section>

        <Section title="Contacto y datos personales">
          <Row label="Correo" value={colaborador.correo_electronico} />
          <Row label="Teléfono" value={colaborador.telefono} />
          <Row label="CP fiscal" value={colaborador.cp_fiscal} />
          <Row label="Domicilio fiscal" value={colaborador.domicilio_fiscal} />
          <Row label="CP actual" value={colaborador.cp_actual} />
          <Row label="Domicilio actual" value={colaborador.domicilio_actual} />
          <Row label="Contacto emergencia" value={ce.nombre ? `${ce.nombre} (${ce.parentesco || '—'}) ${ce.telefono || ''}` : null} />
          <Row label="Número de hijos" value={colaborador.numero_hijos} />
          <Row label="Talla calzado" value={colaborador.talla_calzado} />
          <Row label="Talla playera" value={colaborador.talla_playera} />
          <Row label="Alergias" value={colaborador.alergias} />
        </Section>

        <Section title="Datos administrativos">
          <Row label="Cuenta bancaria" value={colaborador.cuenta_bancaria} />
          <Row label="Infonavit - N° crédito" value={colaborador.infonavit_numero_credito} />
          <Row label="Infonavit - Tipo descuento" value={colaborador.infonavit_tipo_descuento} />
          <Row label="Número de locker" value={colaborador.numero_locker} />
          <Row label="Candado empresa" value={colaborador.candado_empresa} />
        </Section>

        <Section title="Beneficiarios">
          <Row label="Beneficiario 1" value={benefText(b1)} />
          <Row label="Beneficiario 2" value={benefText(b2)} />
        </Section>

        <Section title="Periodo laboral actual">
          {active ? (
            <>
              <Row label="Número de nómina" value={active.numero_nomina} />
              <Row label="Clasificación" value={active.clasificacion} />
              <Row label="Departamento" value={active.departamento} />
              <Row label="Puesto" value={active.puesto} />
              <Row label="Estatus" value={active.estatus_colaborador} />
              <Row label="Fecha de alta" value={active.fecha_alta} />
              <Row label="Temporalidad contrato" value={active.temporalidad_contrato} />
              <Row label="Fecha término contrato" value={active.fecha_termino_contrato} />
              {hasSalario && <Row label="Salario diario" value={money(active.salario_diario)} />}
              {hasSalario && <Row label="Sueldo mensual" value={money(active.sueldo)} />}
              {hasSalario && <Row label="SD fiscal" value={money(active.sd_fiscal)} />}
              {hasSalario && <Row label="SD fiscal con letra" value={active.sd_fiscal_con_letra} />}
            </>
          ) : (
            <div className="col-span-full text-sm text-slate-500">Sin periodo activo (colaborador en Baja).</div>
          )}
        </Section>

        {history.length > 0 && (
          <Collapsible open={histOpen} onOpenChange={setHistOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="colab-history-toggle">
                <History className="w-4 h-4" /> Historial de periodos anteriores ({history.length})
                <ChevronDown className={`w-4 h-4 transition-transform ${histOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-2" data-testid="colab-history-content">
              {history.map((p, i) => {
                const pHasSalario = Object.prototype.hasOwnProperty.call(p, 'salario_diario');
                return (
                  <div key={p.id || i} className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Row label="Nómina" value={p.numero_nomina} />
                    <Row label="Puesto" value={p.puesto} />
                    <Row label="Departamento" value={p.departamento} />
                    <Row label="Alta" value={p.fecha_alta} />
                    <Row label="Baja" value={p.fecha_baja} />
                    <Row label="Causa baja" value={p.causa_baja} />
                    {pHasSalario && <Row label="Salario diario" value={money(p.salario_diario)} />}
                  </div>
                );
              })}
              <p className="text-xs text-slate-400 italic">Los periodos cerrados son de solo lectura.</p>
            </CollapsibleContent>
          </Collapsible>
        )}
      </DialogContent>
    </Dialog>
  );
}
